/**
 * 草图圆角模式枚举。
 */
export enum SketchCornerMode {
    /** 圆角。 */
    Fillet = 'sketch-fillet',
    /** 倒角。 */
    Chamfer = 'sketch-chamfer',
}

/**
 * 判断是否为草图圆角模式。
 * @param command 命令字符串。
 * @returns 是否为草图圆角模式。
 */
export function isSketchCornerMode(command: string): command is SketchCornerMode {
    return Object.values(SketchCornerMode).includes(command as SketchCornerMode);
}

/**
 * 草图倒角类型枚举。
 */
export enum SketchChamferType {
    /** 角度-距离。 */
    AngleDistance = 'angle-distance',
    /** 距离-距离。 */
    DistanceDistance = 'distance-distance',
    /** 等距。 */
    EqualDistance = 'equal-distance',
}

//------ 草图圆角实体面板快照 开始------
/**
 * 草图圆角/倒角实体引用。
 *
 * 说明：该引用只暴露稳定对象 ID 与可选边段索引，避免 UI 或命令状态直接持有 Three.js 对象。
 */
export interface SketchCornerEntityRef {
    /** 草图实体对象 ID。 */
    objectId: string;
    /** 子边段索引；普通直线没有子边段时为 null。 */
    segmentIndex: number | null;
}

/**
 * 草图圆角/倒角选择列表项类型。
 */
export const SketchCornerSelectionItemKind = {
    /** 已选择的草图直线。 */
    Line: 'line',
    /** 已选择的草图圆弧。 */
    Arc: 'arc',
    /** 待确认的草图圆角预览。 */
    FilletPreview: 'fillet-preview',
} as const;

/**
 * 草图圆角/倒角选择列表项类型值。
 */
export type SketchCornerSelectionItemKind = typeof SketchCornerSelectionItemKind[
    keyof typeof SketchCornerSelectionItemKind
];

/**
 * 草图圆角/倒角选择阶段。
 */
export enum SketchCornerSelectionPhase {
    /** 命令未激活。 */
    Idle = 'idle',
    /** 等待选择第一条实体。 */
    SelectFirstEntity = 'select-first-entity',
    /** 已选择第一条实体，等待第二条实体。 */
    SelectSecondEntity = 'select-second-entity',
    /** 已生成待确认圆角预览。 */
    PendingFilletPreview = 'pending-fillet-preview',
}

/**
 * 草图圆角/倒角 PropertyManager 选择列表项。
 */
export interface SketchCornerSelectionItem {
    /** 列表项唯一 ID。 */
    id: string;
    /** 列表展示文案。 */
    label: string;
    /** 列表项语义类型。 */
    kind: SketchCornerSelectionItemKind;
    /** 关联草图实体对象 ID；预览项可为空。 */
    objectId: string | null;
    /** 子边段索引；普通直线、圆弧与预览项为 null。 */
    segmentIndex: number | null;
}

/**
 * 草图圆角/倒角控制器快照。
 *
 * 设计说明：
 * 1. 该快照只包含 PropertyManager 需要展示的普通数据；
 * 2. 不暴露 Three.js 对象，避免 UI 响应式系统代理渲染核心对象；
 * 3. 圆角预览项仅表达待确认命令状态，不代表正式草图实体。
 */
export interface SketchCornerControllerSnapshot {
    /** 当前命令是否激活。 */
    active: boolean;
    /** 当前草图圆角/倒角模式。 */
    mode: SketchCornerMode;
    /** 当前选择阶段。 */
    phase: SketchCornerSelectionPhase;
    /** PropertyManager 中“要圆角化/倒角化的实体”列表。 */
    selectedItems: SketchCornerSelectionItem[];
    /** 当前第一条实体引用。 */
    firstSelection: SketchCornerEntityRef | null;
    /** 当前是否存在待确认圆角预览。 */
    hasPendingFilletPreview: boolean;
    /** 当前是否可以通过 PropertyManager 勾确认正式圆角。 */
    canConfirm: boolean;
}
//------ 草图圆角实体面板快照 结束------

/**
 * 草图圆角参数。
 *
 * 说明：长度值统一使用场景单位，PropertyManager 负责在输入显示层进行毫米和场景单位转换。
 */
export interface SketchFilletOptions {
    /** 圆角半径，单位为场景单位。 */
    radius: number;
    /** 是否保留受约束角点。 */
    keepConstrainedCorners: boolean;
    /** 是否为每个圆角单独标注尺寸。 */
    dimensionEachFillet: boolean;
}

/**
 * 草图倒角参数。
 *
 * 说明：长度值统一使用场景单位，角度使用角度制，便于与 SolidWorks PropertyManager 展示保持一致。
 */
export interface SketchChamferOptions {
    /** 草图倒角类型。 */
    chamferType: SketchChamferType;
    /** 倒角距离 1，单位为场景单位。 */
    distance1: number;
    /** 倒角距离 2，单位为场景单位。 */
    distance2: number;
    /** 角度-距离模式的角度值，单位为度。 */
    angleDeg: number;
}
